import React from 'react';

import { Box, Flex } from '@lightspeed/flame/Core';
import { IconCheckmark } from '@lightspeed/flame/Icon/Checkmark';
import { IconExternalLink } from '@lightspeed/flame/Icon/ExternalLink';
import { Text } from '@lightspeed/flame/Text';

import { createReactRenderer } from 'helpers/react-renderer';

import { MASNotification } from '../Notification';
import { useLocale } from '../shared/useLocale';
import {
  BoldHeader,
  IconWrapper,
  StyledButton,
  StyledCard,
  StyledDescription,
  StyledFlex,
  StyledInvoiceCard,
  UppercaseSubHeading,
} from './index.style';
import InvoiceTable from './InvoiceTable';
import { MerchantAccountSpace } from './MerchantAccountSpace';

enum AccountStatus {
  // Past grace period (locked)
  Suspended = 'Suspended',
  // In grace period (30 days), past due
  Overdue = 'Overdue',
  // Invoice has been sent, 7 days or less before due date
  Due = 'Due',
  // 8-30+ days before due date of invoice
  Active = 'Active',
}

export const useBannerStatus = () => {
  const [accountStatus, setAccountStatus] = React.useState<AccountStatus | null>(null);
  const [formattedDate, setFormattedDate] = React.useState('');
  const locale = useLocale();

  // yyyy-mm-dd format
  const { customerAccountExpiryDate = '' } = window.merchantos.session.sessData || {};

  const dateFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  } as Intl.DateTimeFormatOptions;

  React.useEffect(() => {
    if (customerAccountExpiryDate === '') {
      setAccountStatus(AccountStatus.Active);
      return;
    }

    const today = new Date().getTime();
    const modifiedCustomerAccountExpiryDate = new Date(`${customerAccountExpiryDate}T00:00:00`);
    const expirationDate = new Date(modifiedCustomerAccountExpiryDate).getTime();
    const diffInDays = Math.floor((expirationDate - today) / 1000 / (60 * 60 * 24));

    if (diffInDays >= 8) {
      setAccountStatus(AccountStatus.Active);
      setFormattedDate(
        new Date(modifiedCustomerAccountExpiryDate).toLocaleDateString(locale, dateFormatOptions)
      );
    } else if (diffInDays <= 7 && diffInDays > -1) {
      setAccountStatus(AccountStatus.Due);
      setFormattedDate(
        new Date(modifiedCustomerAccountExpiryDate).toLocaleDateString(locale, dateFormatOptions)
      );
    } else if (diffInDays < 0 && diffInDays >= -60) {
      setAccountStatus(AccountStatus.Overdue);
    } else if (diffInDays < -60) {
      setAccountStatus(AccountStatus.Suspended);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  switch (accountStatus) {
    case AccountStatus.Due:
      return (
        <BannerContent
          title="Your account is due on"
          date={formattedDate}
          description={`An invoice was sent by email. Make sure to pay your bill before the due date using the invoice payment form. Once paid, it can take up-to one hour for your account's due date to update.`}
          image={<img src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/settings/invoice-visual.svg" alt="Invoice page" />}
        />
      );
    case AccountStatus.Overdue:
      return (
        <BannerContent
          title="Your account is overdue"
          description={`To keep using Lightspeed, make sure to pay the invoice sent by email as soon as possible using the invoice payment form. Once paid, it can take up-to one hour for your account's due date to update.`}
          image={<img src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/settings/alarm-ringing-visual.svg" alt="Alarm clock ringing" />}
        />
      );
    case AccountStatus.Suspended:
      return (
        <BannerContent
          title="Your account is locked"
          description="Pay the latest invoice sent by email using the invoice payment form. Once paid, wait between 15 minutes and one hour, then log back in to regain access."
          image={<img src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/settings/padlock-and-key-visual.svg" alt="Padlock with key" />}
        />
      );
    case AccountStatus.Active:
    default:
      return (
        <BannerContent
          title="Your billing cycle ends on"
          date={formattedDate}
          description={`You'll receive an invoice before the due date. Pay your bill using the invoice payment form. Once paid, it can take up-to one hour for your account's due date to update.`}
          image={
            <img src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/settings/calendar-visual.svg" alt="Calendar view with date marked" />
          }
        />
      );
  }
};

const BannerContent = ({
  title,
  date,
  description,
  image,
}: {
  title: string;
  date?: string;
  description: string;
  image: JSX.Element;
}) => {
  return (
    <Flex justifyContent="space-between" alignItems="center">
      <StyledFlex>{image}</StyledFlex>
      <Box flex="1 1 auto" ml={`${window.innerWidth > 1200 ? 4 : 1}`} data-test="banner-status">
        <BoldHeader>
          {title} <var>{date && date}</var>
        </BoldHeader>
        <StyledDescription>{description}</StyledDescription>
        <StyledButton
          variant="secondary"
          fill
          href="https://billing.lightspeedservices.io/pay"
          target="_blank"
          data-test="banner-status-button"
          id="payYourInvoiceButton"
        >
          Pay your invoice
          <IconExternalLink color="#fff" />
        </StyledButton>
      </Box>
    </Flex>
  );
};

const SubscriptionStatus = () => {
  const isSubscriptionInvoiceServiceActive: boolean = window.lsCloud.Modules.hasModule(
    'subscription.invoice_service'
  );

  return (
    <Flex
      maxWidth="66rem"
      flexDirection="column"
      alignItems="flex-start"
      px="1.125rem"
      py="1.875rem"
      margin="0 auto"
    >
      <Flex flexDirection="column" width="100%">
        <Box mb={7}>
          <BoldHeader data-test="page-header">Billing &amp; Subscription</BoldHeader>
          <StyledDescription data-test="page-sub-header">
            Review the status of your account, pay your invoice and manage your subscription.
          </StyledDescription>
          <StyledCard>{useBannerStatus()}</StyledCard>
        </Box>
        {isSubscriptionInvoiceServiceActive &&
          window.merchantos.session.hasRight('admin_shops') && (
            <Box className="notranslate">
              <StyledInvoiceCard>
                <InvoiceTable />
              </StyledInvoiceCard>
            </Box>
          )}
        <Box mb={7}>
          <UppercaseSubHeading mb={2} data-test="page-sub-header-subscription-mgmnt">
            Subscription Management
          </UppercaseSubHeading>
          <StyledDescription mb={2}>
            Need help with your account?{' '}
            <a
              href="https://www.lightspeedhq.com/contact/"
              rel="noreferrer"
              target="_blank"
              style={{ fontWeight: 'bold', color: '#2E61DE', display: 'inline-flex' }}
              data-test="page-sub-header-subscription-mgmnt-link"
            >
              Contact us
              <span style={{ marginLeft: '4px' }}>
                <IconExternalLink color="#2E61DE" />
              </span>
            </a>
          </StyledDescription>
          <StyledDescription>
            Want to{' '}
            <a id="openCancelSubscriptionModal" href="#">
              cancel your subscription
            </a>
            ? We&apos;ll help you through the process.
          </StyledDescription>
          <Flex
            id="requestCancelSubscriptionConfirmation"
            className="alert action-complete hidden"
            height="54px"
            mt={2}
          >
            <IconWrapper>
              <IconCheckmark baseColor="#fff" size="0.75rem" />
            </IconWrapper>
            <Text p="0px !important" ml="12px !important" fontSize="14px !important">
              Thanks for letting us know. We&apos;ll get in touch soon to guide you through the next
              steps.
            </Text>
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};

const BillingAndSubscription = () => {
  const isMerchantAccountSpaceActive: boolean = window.lsCloud.Modules.hasModule(
    'subscription.merchant_account_space'
  );

  React.useEffect(() => {
    window.replaceBreadcrumbs([
      {
        type: 'ui_tab',
        title: 'Settings',
        name: 'admin',
      },
      {
        type: 'react',
        title: isMerchantAccountSpaceActive ? 'Subscription and billing' : 'Billing & Subscription',
        name: 'subscription_billing',
      },
    ]);
  }, [isMerchantAccountSpaceActive]);

  return (
    <>
      {isMerchantAccountSpaceActive ? (
        <Box maxWidth="1300px" margin="0 auto">
          <MASNotification />
          <MerchantAccountSpace />
        </Box>
      ) : (
        <SubscriptionStatus />
      )}
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: BillingAndSubscription,
  selector: '[data-react-root="BillingAndSubscription"]',
});

export default BillingAndSubscription;

history.onChangeState(
  [
    {
      form_name: 'ui_tab',
      tab: 'subscription',
    },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
